import { formatLocalDateTime } from '../../utils/localDateTime';
import React, { useEffect, useState } from 'react';
import { collection, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import type { Organization, UserProfile } from '@types';
import { db } from '../../../firebaseConfig';
import SupportChatModal, { type SupportChatRecord, type SupportManager } from './SupportChatModal';

const date = (value: unknown) => formatLocalDateTime(value, 'Recently');

const ClientSupportSection: React.FC<{ organization: Organization; currentUser: UserProfile; chatsOnly?: boolean }> = ({ organization, currentUser, chatsOnly = false }) => {
    const [chats, setChats] = useState<SupportChatRecord[]>([]);
    const [managers, setManagers] = useState<SupportManager[]>([]);
    const [openChat, setOpenChat] = useState<SupportChatRecord | null>(null);
    const [creating, setCreating] = useState(false);
    const [pendingChatId, setPendingChatId] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        const chatsQuery = query(collection(db, 'support_chats'), where('organizationId', '==', organization.id), where('participantUid', '==', currentUser.uid), orderBy('lastMessageAt', 'desc'));
        return onSnapshot(chatsQuery, snapshot => {
            const next = snapshot.docs.map(document => ({ id: document.id, ...document.data() } as SupportChatRecord));
            setChats(next);
            setOpenChat(previous => previous ? next.find(chat => chat.id === previous.id) || previous : null);
        }, snapshotError => setError(snapshotError.message));
    }, [currentUser.uid, organization.id]);

    useEffect(() => {
        if (!pendingChatId)
            return;
        const created = chats.find(chat => chat.id === pendingChatId);
        if (created) {
            setOpenChat(created);
            setPendingChatId('');
        }
    }, [chats, pendingChatId]);

    useEffect(() => {
        const callable = httpsCallable(getFunctions(undefined, 'us-central1'), 'manageSupportChat');
        void callable({ action: 'listManagers', data: {} })
            .then(response => setManagers((response.data as any)?.managers || []))
            .catch(loadError => setError(loadError.message || 'Assigned consultant could not be loaded.'));
    }, [organization.id]);

    return (
        <section className={chatsOnly ? 'min-w-0' : 'border-t border-slate-200 pt-8 mt-8'}>
            {chatsOnly && <div className="mb-4"><h2 className="text-lg font-extrabold text-slate-900">Consultant Chats</h2><p className="mt-1 text-sm text-slate-500">Message your assigned consultant.</p></div>}
            {!chatsOnly && <div className="mb-6">
                <h3 className="text-lg font-bold text-slate-800">Support and Services</h3>
                <p className="mt-1 text-sm text-slate-500">Contact your assigned consultant and review service options.</p>
            </div>}
            {error && <p className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                <div className={`rounded-2xl border border-slate-200 bg-white shadow-sm ${chatsOnly ? 'flex h-[32rem] flex-col lg:col-span-3' : 'lg:col-span-1'}`}>
                    <div className="border-b border-slate-200 p-5">
                        <div className="flex items-center justify-between gap-3">
                            <div><h4 className="font-extrabold text-slate-900">{chatsOnly ? 'Conversations' : 'Consultant Chats'}</h4><p className="mt-1 text-xs text-slate-500">Messages with your assigned consultant.</p></div>
                            <button type="button" onClick={() => setCreating(true)} disabled={!managers.length} className="rounded-lg bg-blue-600 px-3 py-2 text-xs font-bold text-white hover:bg-blue-700 disabled:opacity-40">New Chat</button>
                        </div>
                    </div>
                    <div className={`${chatsOnly ? 'flex-1' : 'max-h-80'} divide-y divide-slate-100 overflow-y-auto`}>
                        {chats.map(chat => (
                            <button key={chat.id} type="button" onClick={() => setOpenChat(chat)} className="block w-full p-4 text-left hover:bg-slate-50">
                                <div className="flex items-center justify-between gap-2"><p className="truncate text-sm font-bold text-slate-800">{chat.managerName}</p>{chat.unreadForClient && <span className="h-2 w-2 shrink-0 rounded-full bg-blue-500" />}</div>
                                <p className="mt-1 line-clamp-2 text-xs text-slate-500">{chat.lastMessagePreview}</p>
                                <p className="mt-2 text-[10px] text-slate-400">{date(chat.lastMessageAt)}</p>
                            </button>
                        ))}
                        {!chats.length && <p className="p-6 text-center text-sm text-slate-400">No consultant chats yet.</p>}
                    </div>
                </div>
                {!chatsOnly && <div className="flex min-h-64 items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-gradient-to-br from-slate-50 to-blue-50 p-8 text-center lg:col-span-2">
                    <div><div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-100 text-2xl">◆</div><h4 className="text-xl font-extrabold text-slate-900">Service Requests</h4><p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-slate-500">Request scoping, quoting, and service fulfillment will be available here soon.</p><span className="mt-4 inline-flex rounded-full bg-white px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-blue-700 shadow-sm ring-1 ring-blue-100">Coming Soon</span></div>
                </div>}
            </div>
            {creating && <SupportChatModal mode="client" managers={managers} onClose={() => setCreating(false)} onCreated={chatId => { setCreating(false); setPendingChatId(chatId); }} />}
            {openChat && <SupportChatModal mode="client" chat={openChat} onClose={() => setOpenChat(null)} />}
        </section>
    );
};

export default ClientSupportSection;
